import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { useLoadingBarState } from '../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { IKPITemplate } from '../../../data-models/kpi-template.data-model';
import { getErrorMessage } from '../../../services/queryHelpers';
import { kpiTemplateAssignmentsListStateJ } from '../../../services/state/KPI/KPICompaniesStateJ';
import { kpiTemplatesListStateJ } from '../../../services/state/KPI/KPITemplatesStateJ';
import { LoadingId } from '../../../types';
import {
  createKPITemplate,
  deleteKPITemplate,
  fetchKPITemplates,
  fetchTemplatesAssignments,
  updateKPITemplate,
} from '../../../services/queries/KPITemplatesQueries';

import { useInvalidateKPIRequests } from '../components/KPIRequests/KPIRequestResponse/hooks/useInvalidateKPIRequests';

// FIXME: MAGGIE-4576
export function useKPITemplateActions() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const invalidateRequests = useInvalidateKPIRequests();

  const createKPITemplateAction = useAtomCallback(
    useCallback(
      async (get, set, payload: Partial<IKPITemplate>) => {
        actions.startLoading(LoadingId.createKPITemplate);
        try {
          const preparedPayload = prepareCreatePayload(payload);

          const response = (await createKPITemplate(preparedPayload)) as IKPITemplate;

          const current = await get(kpiTemplatesListStateJ);
          set(kpiTemplatesListStateJ, [...(current ?? []), response]);

          pushSuccessToast({ message: `Template "${payload.name}" was created successfully.` });
          return { id: response.id };
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Failed to create template.');
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.createKPITemplate);
        }
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );

  const updateKPITemplateAction = useAtomCallback(
    useCallback(
      async (get, set, payload: Partial<IKPITemplate>) => {
        actions.startLoading(LoadingId.updateKPITemplate);
        try {
          const preparedPayload = prepareUpdatePayload(payload);
          const response = (await updateKPITemplate(preparedPayload)) as IKPITemplate;

          const current = await get(kpiTemplatesListStateJ);
          const newValues =
            current?.map((value) => {
              if (value.id === response.id) {
                return response;
              }
              return value;
            }) ?? [];
          set(kpiTemplatesListStateJ, [...newValues]);

          pushSuccessToast({ message: `Template "${payload.name}" was updated successfully.` });
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Failed to update template.');
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.updateKPITemplate);
        }
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );

  const deleteKPITemplateAction = useAtomCallback(
    useCallback(
      async (_get, set, payload: IKPITemplate) => {
        actions.startLoading(LoadingId.deleteTemplate);

        try {
          if (!payload.id) {
            throw new Error('Missing data');
          }
          const response = await deleteKPITemplate(payload.id);
          const templates = (await fetchKPITemplates()) as IKPITemplate[];
          set(kpiTemplatesListStateJ, templates);
          invalidateRequests();
          set(kpiTemplateAssignmentsListStateJ, await fetchTemplatesAssignments());

          const successMsg = `Template "${payload?.name}" was removed`;

          pushSuccessToast({
            message: successMsg,
          });
          return response;
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, `Failed to delete template ${payload?.name}.`);
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.deleteTemplate);
        }
      },
      [actions, invalidateRequests, pushErrorToast, pushSuccessToast]
    )
  );

  return {
    updateKPITemplateAction,
    createKPITemplateAction,
    deleteKPITemplateAction,
  };
}

const prepareCreatePayload = (payload: Partial<IKPITemplate>) => {
  return {
    isManagedService: payload.isManagedService,
    name: payload.name,
    sections: payload.sections,
  };
};

const prepareUpdatePayload = (payload: Partial<IKPITemplate>) => {
  return {
    uuid: payload.uuid,
    id: payload.id,
    name: payload.name,
    sections: payload.sections,
    isManagedService: payload.isManagedService,
  };
};
